import type { CSSProperties } from 'react';
import { cn } from '@/shared/lib/utils';
import { type PinnedColumnInfo, WIDTH_PRESETS, type WidthPreset } from './types';

// types.tsから必要な定数を再エクスポート
export { WIDTH_PRESETS };

/** 左固定カラム境界の影（右側にグラデーション） */
const PINNED_LEFT_SHADOW =
  "after:content-[''] after:pointer-events-none after:absolute after:right-0 after:top-0 after:bottom-0 after:w-1.5 after:translate-x-full after:bg-gradient-to-r after:from-black/6 after:to-transparent";

/** 右固定カラム境界の影（左側にグラデーション） */
const PINNED_RIGHT_SHADOW =
  "before:content-[''] before:pointer-events-none before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1.5 before:-translate-x-full before:bg-gradient-to-l before:from-black/6 before:to-transparent";

/**
 * ピン留めカラムのクラス名を生成する
 *
 * @param pinned - ピン留め情報
 * @param options.isCell - td要素の場合true（bg-whiteを追加）
 * @returns Tailwindクラス名（ピン留めなしの場合はundefined）
 */
export const getPinnedClassName = ({
  pinned,
  isCell = false,
}: {
  pinned: PinnedColumnInfo | undefined;
  isCell?: boolean;
}): string | undefined => {
  if (!pinned) return undefined;
  return cn(
    isCell && 'bg-white',
    pinned.isLastLeftPinned && PINNED_LEFT_SHADOW,
    pinned.isFirstRightPinned && PINNED_RIGHT_SHADOW,
  );
};

/**
 * ハイライト行のピン留めセルに適用するシャドウクラスを生成する
 *
 * ピン留めセルは bg-white が適用されるため、行の inset shadow が隠れる。
 * セル個別にシャドウを適用して緑枠を継続させる。
 *
 * @param isFirstCell - 行の最初のセルかどうか
 * @param isLastCell - 行の最後のセルかどうか
 * @returns Tailwindシャドウクラス名
 */
export const getHighlightedPinnedClassName = ({
  isFirstCell,
  isLastCell,
}: {
  isFirstCell: boolean;
  isLastCell: boolean;
}): string => {
  if (isFirstCell && isLastCell) {
    return 'shadow-[inset_0_0_0_2px_var(--color-border-accent)]';
  }
  if (isFirstCell) {
    return 'shadow-[inset_2px_0_0_0_var(--color-border-accent),inset_0_2px_0_0_var(--color-border-accent),inset_0_-2px_0_0_var(--color-border-accent)]';
  }
  if (isLastCell) {
    return 'shadow-[inset_-2px_0_0_0_var(--color-border-accent),inset_0_2px_0_0_var(--color-border-accent),inset_0_-2px_0_0_var(--color-border-accent)]';
  }
  return 'shadow-[inset_0_2px_0_0_var(--color-border-accent),inset_0_-2px_0_0_var(--color-border-accent)]';
};

/**
 * カラムスタイルとウェイトスタイルをマージする
 *
 * @param columnStyle - 基本的なカラムスタイル
 * @param weightedStyle - ウェイト指定によるスタイル
 * @returns マージされたスタイル
 */
export const mergeColumnStyles = (
  columnStyle: CSSProperties | undefined,
  weightedStyle: CSSProperties | undefined,
): CSSProperties | undefined => {
  if (weightedStyle) {
    return { ...columnStyle, ...weightedStyle };
  }
  return columnStyle;
};

/**
 * カラム幅を取得するヘルパー関数
 *
 * @param width - 数値、WidthPreset、または 'auto'
 * @returns カラム幅（px）、'auto'、または undefined
 */
export const getWidth = (
  width: number | WidthPreset | 'auto' | undefined,
): number | 'auto' | undefined => {
  if (width === undefined) return undefined;
  if (typeof width === 'number') return width;
  // 'auto' の場合はそのまま返す（均等配置用）
  if (width === 'auto') return 'auto';
  return WIDTH_PRESETS[width];
};
